<template>
  <section class="container mx-auto">
    <header class="mb-8 flex items-center justify-between">
      <h3 class="text-3xl font-black tracking-wide uppercase">{{ t('titles.categories') }}</h3>
      <span v-if="loading" class="mt-1 block text-sm text-gray-500">{{ t('messages.loading') }}</span>
    </header>

    <div v-if="error" class="mt-4 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
      {{ t('messages.errorFetchingCategories') }}: {{ error }}
    </div>

    <div v-else class="relative">

      <!-- Horizontal scroller -->
      <div ref="scroller" class="overflow-x-auto scroll-smooth mmc-scroller" tabindex="0" role="region"
           :aria-label="t('aria.categoriesRegion')" style="scroll-snap-type: x mandatory;">
        <ul class="flex items-center gap-4 sm:gap-4 list-none" style="white-space: nowrap;">
          <li v-for="cat in categories" :key="cat.id" class="shrink-0" style="scroll-snap-align: start;">
            <a :href="categoryHref(cat)"
               class="group inline-flex w-24 sm:w-40 pt-7 flex-col items-center focus:outline-none">
              <span
                  class="relative inline-flex h-20 w-20 sm:h-28 sm:w-28 items-center justify-center overflow-hidden rounded-full bg-white  transition-transform duration-200 group-hover:scale-105"
                  :aria-label="cat.name">
                <img v-if="categoryImage(cat)" :src="categoryImage(cat)" :alt="cat.name"
                     class="h-full w-full object-cover" loading="lazy" decoding="async"/>
                <span v-else class="text-xl sm:text-2xl font-semibold text-gray-500">{{ initials(cat.name) }}</span>
              </span>
              <span class="mt-3 h-10 line-clamp-2 text-center  text-[18px]">{{ cat.name }}</span>
            </a>
          </li>
        </ul>
      </div>
      <div class="mt-4 flex justify-center">
        <div class="inline-flex items-center gap-3">
          <button v-if="canScrollLeft" @click="scrollPrev" class="mmlm-arrow"
                  :aria-label="t('aria.scrollLeft') || 'Scroll links'">


            <svg xmlns="http://www.w3.org/2000/svg" width="19.058" height="16" viewBox="0 0 19.058 16"
                 class="transition-colors duration-300" style="transform: rotate(180deg);">
              <path id="arrow-right-sharp-regular"
                    d="M18.333,77.127l.725-.725-.725-.725-6.552-6.552-.725-.725L9.61,69.846l.725.725,4.8,4.807H0v2.047H15.138l-4.807,4.8-.725.725L11.052,84.4l.725-.725Z"
                    transform="translate(0 -68.4)" fill="#fff"/>
            </svg>


          </button>
          <button v-if="canScrollRight" @click="scrollNext" class="mmlm-arrow"
                  :aria-label="t('aria.scrollRight') || 'Scroll rechts'">

            <svg xmlns="http://www.w3.org/2000/svg" width="19.058" height="16" viewBox="0 0 19.058 16"
                 class="transition-colors duration-300">
              <path id="arrow-right-sharp-regular"
                    d="M18.333,77.127l.725-.725-.725-.725-6.552-6.552-.725-.725L9.61,69.846l.725.725,4.8,4.807H0v2.047H15.138l-4.807,4.8-.725.725L11.052,84.4l.725-.725Z"
                    transform="translate(0 -68.4)" fill="#fff"/>
            </svg>
          </button>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { onMounted, ref } from 'vue'
import WpApiClient from '../lib/wp-api.js'
import { t as i18nT } from '../i18n'

const t = (key) => i18nT(key)

const props = defineProps({
  apiBase: { type: String, default: '' },
  buildCategoryHref: { type: Function, default: null },
})

const api = new WpApiClient({ 
  base: props.apiBase,
  cacheBust: true
})

const imageFieldCandidates = ['image', 'thumbnail', 'term_image', 'icon'];

function getImageFromAcf(acf) {
  if (!acf || typeof acf !== 'object') return '';
  for (const key of imageFieldCandidates) {
    const img = acf[key];
    if (!img) continue;
    if (typeof img === 'string') return img;
    if (img?.sizes?.medium) return img.sizes.medium;
    if (img?.url) return img.url;
  }
  return '';
}

function initials(name) {
  if (!name) return '';
  return name
    .split(/\s+/)
    .slice(0, 2)
    .map(p => p.charAt(0).toUpperCase())
    .join('');
}

const categories = ref([])
const loading = ref(true)
const error = ref('')

// Horizontal scroll container
const scroller = ref(null)
const canScrollLeft = ref(false)
const canScrollRight = ref(false)

function updateScrollButtons() {
  const el = scroller.value;
  if (!el) return;
  canScrollLeft.value = el.scrollLeft > 0;
  canScrollRight.value = el.scrollLeft + el.clientWidth < el.scrollWidth - 5;
}

function scrollByDelta(delta){
  const el = scroller?.value
  if (!el) return
  try { el.scrollBy({ left: delta, behavior: 'smooth' }); }
  catch(e){ el.scrollLeft += delta }
}
function scrollPrev(){
  const el = scroller?.value
  const w = el ? el.clientWidth : 320
  scrollByDelta(-Math.max(240, Math.floor(w * 0.9)))
}
function scrollNext(){
  const el = scroller?.value
  const w = el ? el.clientWidth : 320
  scrollByDelta(Math.max(240, Math.floor(w * 0.9)))
}

async function fetchCategories() {
  loading.value = true
  error.value = ''
  try {
    const raw = await api.getCategories({ taxonomy: 'materiaal_categorie', perPage: 100, fields: 'id,name,slug,count,description,parent,acf,image_url' })
    categories.value = raw.map(c => ({
      ...c,
      imageUrl: c?.image_url || getImageFromAcf(c?.acf) || c?.image || c?.thumbnail || '',
    }));
  } catch (e) {
    error.value = e?.message || String(e)
  } finally {
    loading.value = false
  }
}

function categoryHref(cat) {
  if (typeof props.buildCategoryHref === 'function') return props.buildCategoryHref(cat)
  // Prefer the Vue-powered materials route with pretty slug
  return `/materialen/${cat.slug}`
}

function categoryImage(cat) {
  return cat?.image_url || cat?.imageUrl || '';
}

onMounted(() => {
  fetchCategories().then(() => updateScrollButtons());
  const el = scroller.value;
  if (el) el.addEventListener('scroll', updateScrollButtons);
});
</script>

<style scoped>
/* Optional: truncate description */
.line-clamp-3 {
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

/* Hide any list markers/bullets inside the horizontal scroller, even if added by global theme CSS */
.mmc-scroller ul,
.mmc-scroller li {
  list-style: none !important;
}
.mmc-scroller ul { padding-left: 0 !important; margin-left: 0 !important; }
.mmc-scroller li::marker { content: '' !important; }
.mmc-scroller li::before { content: none !important; display: none !important; }

.mmc-scroller{ -ms-overflow-style: none; scrollbar-width: none; }
/* Ensure no custom track/thumb leaks in from global CSS */
.mmc-scroller::-webkit-scrollbar { width: 0; height: 0; background: transparent; }
.mmc-scroller::-webkit-scrollbar-track { background: transparent; border: none; box-shadow: none; }
.mmc-scroller::-webkit-scrollbar-thumb { background: transparent; border: none; box-shadow: none; }
/* Pijlknoppen: vierkante zwarte blokjes met wit pictogram/tekst */
.mmlm-arrow {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  background: #000;
  color: #fff;
  border: 1px solid #000;
}
.mmlm-arrow:hover { background:#111; border-color:#111; }
</style>
